import { useEffect } from 'react'

import classNames from 'classnames'

import MilesTracker from '@src/components/MilesTracker'
import ReferralCodeInput from '@src/components/ReferralCodeInput'
import SandDollarsTracker from '@src/components/SandDollarsTracker'
import { SwapButton } from '@src/components/buttons/SwapButton'
import ChevronLeftIcon from '@src/components/icons/ChevronLeft'
import InfoIcon from '@src/components/icons/Info'
import { ChainId, NATIVE_TOKEN_ADDRESS, WRAPPED_GAS_TOKENS } from '@src/configs/chainInfo'
import { ResultCode, StepNames } from '@src/enums'
import { LiquiditySource } from '@src/enums'
import { useWalletConnections } from '@src/hooks/useWalletConnections'
import TransactionDetails from '@src/pages/Exchange/components/SwapInProgressFlow/TransactionDetails'
import useExchangeBalances from '@src/pages/Exchange/hooks/useExchangeBalances'
import { useSwapContract } from '@src/pages/Exchange/hooks/useSwapContract'
import { IExchangeData } from '@src/types/exchange'
import { IFee } from '@src/types/fee'
import { ISandDollarsData } from '@src/types/order'
import { Balance, isFeatureEnabled } from '@src/utils'
import { isUnWrapOnly, isWrapOnly } from '@src/utils/exchangeUtils'
import { getAllowance } from '@src/utils/getAllowance'
import { createContextLogger } from '@src/utils/logger'
import { useApplicationStore } from '@src/zustand_stores/application.store'
import { useHistoryStore } from '@src/zustand_stores/history.store'

const transactionLogger = createContextLogger('TransactionSummary')

interface ITransactionSummaryProps {
    onClose: () => void
    onConfirm: () => Promise<void>
    currentStep: StepNames
    sellData: IExchangeData
    buyData: IExchangeData
    fees: IFee[]
    destinationAddress?: string
    miles?: number
    sandDollars?: ISandDollarsData
    isOpen: boolean
    liquiditySource: LiquiditySource
    isCCTPTransfer: boolean
    isGasEstimationFetched: boolean
}

export default function TransactionSummary({
    onClose,
    onConfirm,
    currentStep,
    sellData,
    buyData,
    fees,
    destinationAddress,
    miles,
    sandDollars,
    isOpen,
    liquiditySource,
    isCCTPTransfer,
    isGasEstimationFetched,
}: ITransactionSummaryProps) {
    // Zustand store variables
    const { exchangeId, setWaitingForConnectedWalletInteraction } = useApplicationStore()
    const updateHistory = useHistoryStore(state => state.updateHistory)

    // Wallet variable
    const { tronWalletAddress, solanaWalletAddress, evmWalletAddress } = useWalletConnections()

    const swapContract = useSwapContract(sellData, liquiditySource || LiquiditySource.unavailable)

    const {
        nativeBalance,
        wrappedNativeBalance,
        isNativeBalanceLoading,
        isWrappedNativeBalanceLoading,
        refreshAllBalancesAndWait,
    } = useExchangeBalances()

    const tokenBalance = new Balance(sellData.token).setValue(sellData.token.userBalance ?? BigInt(0))

    // Check balances, approvals, and determine initial step
    useEffect(() => {
        const refreshBalances = async () => {
            await refreshAllBalancesAndWait()
        }

        if (!isOpen) {
            refreshBalances()
            return
        }

        const determineStep = async () => {
            transactionLogger.info('Determining step...')
            // Don't change anything if we're in a pending state
            if (isPendingStep(currentStep)) {
                return
            }

            if (!evmWalletAddress || !sellData.token?.address || !sellData.value) {
                return
            }

            // Add check for Solana CCTP_DIRECT and Jupiter swaps
            // Both should skip the approval step and go directly to CONFIRM_SWAP
            if (
                (sellData.chainId === ChainId.Solana && liquiditySource === LiquiditySource.cctpDirect) ||
                liquiditySource === LiquiditySource.jupiter
            ) {
                updateHistory(exchangeId, {
                    currentStep: StepNames.CONFIRM_SWAP,
                })

                return
            }

            if (isWrapOnly(sellData, buyData)) {
                // Wrap only

                updateHistory(exchangeId, {
                    currentStep: StepNames.DEPOSIT_WETH,
                    result: {
                        status: ResultCode.pending,
                        message: `Wrapping ${sellData.token.symbol}`,
                    },
                })

                return
            } else if (isUnWrapOnly(sellData, buyData)) {
                // Unwrap only
            } else {
                // Every other order book swap

                // Wait for balances to load before checking if user's balance is sufficient
                // Necessary to prevent resulting a failure when one balance loads before the other
                if (isNativeBalanceLoading || isWrappedNativeBalanceLoading) return

                const isNativeToken = sellData.token.address === NATIVE_TOKEN_ADDRESS[sellData.chainId]
                const wrappedToken = WRAPPED_GAS_TOKENS[sellData.chainId]
                const sellAmount = sellData.value

                try {
                    // For native tokens (e.g., POL)
                    if (isNativeToken && wrappedToken && sellData.chainId !== ChainId.Celo) {
                        const wrappedTokenBalance = wrappedNativeBalance?.bigintValue || BigInt(0)
                        const nativeTokenBalance = nativeBalance?.bigintValue || BigInt(0)
                        const sellAmountBigInt = BigInt(sellAmount)

                        // First check if we have enough native balance
                        if (nativeTokenBalance < sellAmountBigInt) {
                            updateHistory(exchangeId, {
                                currentStep: StepNames.UNSTARTED,
                                result: {
                                    status: ResultCode.failure,
                                    message: `Insufficient ${sellData.token.symbol} balance`,
                                },
                            })

                            return
                        }

                        // Check if we have enough wrapped balance
                        if (wrappedTokenBalance >= sellAmountBigInt) {
                            // Check if we need approval for the wrapped token
                            const allowance = await getAllowance(
                                +sellData.chainId,
                                wrappedToken.address,
                                evmWalletAddress,
                                swapContract.address
                            )

                            const needsApproval = BigInt(allowance) < sellAmountBigInt

                            // Set appropriate step

                            updateHistory(exchangeId, {
                                currentStep: needsApproval ? StepNames.APPROVE_TOKENS : StepNames.CONFIRM_SWAP,
                            })

                            return
                        }

                        // Remove the pre-wrap allowance check and proceed directly to wrapping
                        updateHistory(exchangeId, {
                            currentStep: StepNames.DEPOSIT_WETH,
                            result: {
                                status: ResultCode.pending,
                                message: `Wrapping ${sellData.token.symbol}`,
                            },
                        })

                        return
                    }

                    // For non-native tokens
                    const tokenBalanceBigInt = tokenBalance?.bigintValue || BigInt(0)
                    const sellAmountBigInt = BigInt(sellAmount)

                    transactionLogger.info('Token balance and sell amount', {
                        tokenBalanceBigInt: tokenBalanceBigInt.toString(),
                        sellAmountBigInt: sellAmountBigInt.toString(),
                    })

                    // Check token balance first
                    if (tokenBalanceBigInt < sellAmountBigInt) {
                        updateHistory(exchangeId, {
                            currentStep: StepNames.UNSTARTED,
                            result: {
                                status: ResultCode.failure,
                                message: `Insufficient ${sellData.token.symbol} balance`,
                            },
                        })

                        return
                    }

                    transactionLogger.info('Chain ID', { chainId: sellData.chainId })
                    const walletAddress =
                        +sellData.chainId === ChainId.Tron
                            ? tronWalletAddress
                            : +sellData.chainId === ChainId.Solana
                              ? solanaWalletAddress
                              : evmWalletAddress

                    // Then check allowance
                    const allowance = await getAllowance(
                        +sellData.chainId,
                        sellData.token.address,
                        walletAddress,
                        swapContract.address
                    )

                    transactionLogger.info('Allowance details', {
                        allowance,
                        sellValue: sellData.value,
                        chainType:
                            sellData.chainId === ChainId.Solana
                                ? 'solana'
                                : sellData.chainId === ChainId.Tron
                                  ? 'tron'
                                  : 'evm',
                    })

                    const needsApproval = BigInt(allowance) < BigInt(sellData.value)

                    updateHistory(exchangeId, {
                        currentStep: needsApproval ? StepNames.APPROVE_TOKENS : StepNames.CONFIRM_SWAP,
                    })
                } catch (error) {
                    transactionLogger.error('Error checking balances and allowances:', {
                        error,
                    })

                    updateHistory(exchangeId, {
                        currentStep: StepNames.UNSTARTED,
                        result: {
                            status: ResultCode.failure,
                            message: 'Error checking balances and allowances. Please try again.',
                        },
                    })
                }
            }
        }

        determineStep()
    }, [
        evmWalletAddress,
        sellData.token?.address,
        sellData.value,
        sellData.chainId,
        swapContract.address,
        exchangeId,
        wrappedNativeBalance?.bigintValue,
        nativeBalance?.bigintValue,
        tokenBalance?.bigintValue,
        isNativeBalanceLoading,
        isWrappedNativeBalanceLoading,
    ])

    // Handle step progression
    const handleConfirmAsync = async () => {
        try {
            setWaitingForConnectedWalletInteraction(true)

            // Clear any previous error messages when retrying
            if (transactionResult?.status === ResultCode.failure) {
                updateHistory(exchangeId, {
                    result: undefined,
                })
            }

            // If we're at the final confirmation step
            if (currentStep === StepNames.CONFIRM_SWAP) {
                updateHistory(exchangeId, {
                    currentStep: StepNames.CONFIRM_SWAP_PENDING,
                })
            } else if (currentStep === StepNames.DEPOSIT_WETH) {
                updateHistory(exchangeId, {
                    currentStep: StepNames.DEPOSIT_WETH_PENDING,
                })
            } else if (currentStep === StepNames.APPROVE_TOKENS) {
                updateHistory(exchangeId, {
                    currentStep: StepNames.APPROVE_TOKENS_PENDING,
                })
            }

            // Call onConfirm which handles the actual transaction
            await onConfirm()
        } catch (error: any) {
            transactionLogger.error('Transaction error:', { error })

            // Check for user rejection
            const isUserRejection =
                error?.message?.includes('rejected') ||
                error?.message?.includes('user denied') ||
                error?.message?.includes('user cancelled')

            // Determine the appropriate step to revert to
            let revertToStep = currentStep

            if (currentStep === StepNames.DEPOSIT_WETH_PENDING) {
                revertToStep = StepNames.DEPOSIT_WETH
            } else if (currentStep === StepNames.APPROVE_TOKENS_PENDING) {
                revertToStep = StepNames.APPROVE_TOKENS
            } else if (currentStep === StepNames.CONFIRM_SWAP_PENDING) {
                revertToStep = StepNames.CONFIRM_SWAP
            }

            // Update the history with the appropriate step and status
            updateHistory(exchangeId, {
                currentStep: revertToStep,
                result: {
                    status: isUserRejection ? ResultCode.pending : ResultCode.failure,
                    message: isUserRejection
                        ? 'Transaction was rejected. Please try again.'
                        : 'Transaction failed. Please try again.',
                },
            })

            setWaitingForConnectedWalletInteraction(false)
        }
    }

    // Determine if a step is in a pending state
    const isPendingStep = (step: StepNames) => {
        return [
            StepNames.DEPOSIT_WETH_PENDING,
            StepNames.APPROVE_TOKENS_PENDING,
            StepNames.CONFIRM_SWAP_PENDING,
            StepNames.MATCHING,
            StepNames.MATCH_CREATED,
        ].includes(step)
    }

    // Get the current transaction result status, but don't show errors during pending operations
    const rawTransactionResult = useHistoryStore(state => state.history[exchangeId]?.result)
    const transactionResult = isPendingStep(currentStep) ? undefined : rawTransactionResult

    // Update the close handler
    const handleClose = () => {
        const pendingStates = [
            StepNames.DEPOSIT_WETH_PENDING,
            StepNames.APPROVE_TOKENS_PENDING,
            StepNames.CONFIRM_SWAP_PENDING,
            StepNames.MATCHING,
            StepNames.MATCH_CREATED,
        ]

        // Prevent closing if we're in a pending state
        if (pendingStates.includes(currentStep)) {
            return
        }

        // Reset waiting state when modal is closed
        setWaitingForConnectedWalletInteraction(false)
        onClose()
    }

    const handleMetaMaskPopup = (step: StepNames) => {
        updateHistory(exchangeId, {
            currentStep: step,
        })

        setWaitingForConnectedWalletInteraction(true)
    }

    return (
        <div className='swap-progress-container flex h-full max-h-[90vh] flex-col'>
            {/* Scrollable content area with ALL transaction details including header */}
            <div className='swap-progress-content flex-grow overflow-y-auto'>
                {/* Header now inside scrollable area */}
                <div className='swap-progress-header flex w-full items-center px-4 py-3'>
                    <div className='flex-1'>
                        <ChevronLeftIcon
                            width={24}
                            height={24}
                            onClick={handleClose}
                            className={classNames('cursor-pointer', {
                                'cursor-not-allowed opacity-50': isPendingStep(currentStep),
                            })}
                        />
                    </div>
                    <p className='swap-progress-title flex-10 text-2xl font-bold'>Transaction Summary</p>
                    <div className='flex-1'></div>
                </div>

                <div className='swap-progress-details space-y-4 px-4 py-2 text-sm text-mach-accent-forgeround md:space-y-6'>
                    <TransactionDetails
                        sellData={sellData}
                        buyData={buyData}
                        fees={fees}
                        destinationAddress={destinationAddress}
                        currentStep={currentStep}
                        liquiditySource={liquiditySource}
                        isCCTPTransfer={isCCTPTransfer}
                    />
                    <div className='flex w-full flex-col gap-2'>
                        {!!miles && !isFeatureEnabled('USE_TESTNETS_ONLY') && (
                            <MilesTracker miles={miles} tooltipPosition='top-left' />
                        )}
                        {!!sandDollars &&
                            !isFeatureEnabled('USE_TESTNETS_ONLY') &&
                            isFeatureEnabled('SAND_DOLLARS') && (
                                <SandDollarsTracker
                                    sandDollars={sandDollars}
                                    isStablecoinSwap={false}
                                    tooltipPosition='left'
                                />
                            )}
                    </div>
                    {!isFeatureEnabled('USE_TESTNETS_ONLY') && <ReferralCodeInput />}
                </div>
            </div>

            {/* Button - Fixed at bottom with subtle separation */}
            <div className='swap-progress-footer sticky bottom-0 px-4 pb-4 pt-6'>
                {/* Display error message if transaction failed and we're not in a pending state */}
                {transactionResult?.status === ResultCode.failure && !isPendingStep(currentStep) && (
                    <div className='swap-progress-error mb-4 flex items-center'>
                        <div className='mr-2 text-red-500'>
                            <InfoIcon width={16} height={16} />
                        </div>
                        <p className='text-sm text-red-500'>
                            {transactionResult.message || 'Transaction failed. Please try again.'}
                        </p>
                    </div>
                )}

                <SwapButton
                    errorsJSON={{
                        isInsufficientBalance: false,
                        isQuoteInvalid: false,
                        isBuyOutputLoading: false,
                    }}
                    sellData={sellData}
                    buyData={buyData}
                    isGasEstimationFetched={isGasEstimationFetched}
                    destinationAddress={destinationAddress}
                    isCustomRecipientWallet={destinationAddress !== undefined && destinationAddress !== ''}
                    className='w-full'
                    onClick={() => {
                        void handleConfirmAsync()
                    }}
                    currentStep={currentStep}
                    onMetaMaskPopup={handleMetaMaskPopup}
                    disabled={isPendingStep(currentStep)}
                    buttonText={
                        !isPendingStep(currentStep) && transactionResult?.status === ResultCode.failure
                            ? 'Retry'
                            : 'Swap'
                    }
                    liquiditySource={liquiditySource}
                />
            </div>
        </div>
    )
}
